import { DateRange, DateString } from '@/types/state/filter';
import { getAllowedRange } from '.';

const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;

export function isInRange(date: DateString, { from, to }: DateRange): boolean {
  return from <= date && date <= to;
}

export function isDateString(date?: string): date is DateString {
  if (!date) return false;
  return dateFormatRegex.test(date);
}

/**
 * This function validates the date range.
 * @param from - The start date.
 * @param to - The end date.
 * @param isAlltimePresetEnabled - Extends the earliest allowed date back to the "All time" start.
 * @returns The validation result.
 * TODO: drop `isAlltimePresetEnabled` once `allTimePreset` is enabled for everyone.
 */
export const validateDateRange = (from?: string, to?: string, isAlltimePresetEnabled = false) => {
  if (!from || !to) return;

  if (!isDateString(from) || !isDateString(to)) {
    return {
      isValid: false,
      error: 'Invalid date format - please use YYYY-MM-DD',
    };
  }

  const allowedRange = getAllowedRange(isAlltimePresetEnabled);
  if (!isInRange(from, allowedRange) || !isInRange(to, allowedRange)) {
    return {
      isValid: false,
      error: 'Date range not allowed',
    };
  }

  if (from > to) {
    return {
      isValid: false,
      error: 'Starting date cannot be later than the ending date',
    };
  }

  return { isValid: true as const };
};

/**
 * Returns `true` when `to` is later than the same day one year after `from`.
 * Exactly one year apart returns `false`. Both dates must be `YYYY-MM-DD`.
 */
export const isOverAYearApart = (from: string, to: string) => {
  const [startYear, startMonth, startDay] = from.split('-').map(Number);
  const [endYear, endMonth, endDay] = to.split('-').map(Number);

  return (
    endYear > startYear + 1 ||
    (endYear === startYear + 1 &&
      (endMonth > startMonth || (endMonth === startMonth && endDay > startDay)))
  );
};
